import React, {useEffect, useState, useCallback} from 'react';
import {View, ScrollView, StyleSheet} from 'react-native';
import {Appbar, TextInput, Button, Snackbar, Text, Switch, IconButton} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useConfigStore} from '../../store/useConfigStore';
import {colors, spacing} from '../../theme';

const TaxSettingsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {fetchByType, getProperty, updateProperties, isLoading} = useConfigStore();

  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxName, setTaxName] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [taxLines, setTaxLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchByType('TAX');
  }, [fetchByType]);

  useEffect(() => {
    setTaxEnabled(getProperty('TAX', 'TAX_ENABLED') === 'true');
    setTaxName(getProperty('TAX', 'TAX_NAME'));
    setTaxNumber(getProperty('TAX', 'TAX_NUMBER'));
    const linesJson = getProperty('TAX', 'TAX_LINES');
    if (linesJson) {
      try {
        setTaxLines(JSON.parse(linesJson));
      } catch (_) {}
    }
  }, [getProperty, useConfigStore.getState().properties]);

  const addTaxLine = useCallback(() => {
    setTaxLines(prev => [...prev, {name: '', percentage: ''}]);
  }, []);

  const updateTaxLine = useCallback((index, field, value) => {
    setTaxLines(prev => prev.map((line, i) => i === index ? {...line, [field]: value} : line));
  }, []);

  const removeTaxLine = useCallback(index => {
    setTaxLines(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const cleanLines = taxLines
        .filter(l => l.name && l.percentage)
        .map(l => ({name: l.name, percentage: Number(l.percentage)}));

      await updateProperties('TAX', [
        {code: 'TAX_ENABLED', name: 'Tax Enabled', value: String(taxEnabled)},
        {code: 'TAX_NAME', name: 'Tax Name', value: taxName},
        {code: 'TAX_NUMBER', name: 'Tax Number', value: taxNumber},
        {code: 'TAX_LINES', name: 'Tax Lines', value: JSON.stringify(cleanLines)},
      ]);
      setSnackbar(t('config.saved'));
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [taxEnabled, taxName, taxNumber, taxLines, updateProperties, t]);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('config.taxSettings')} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView style={styles.content}>
        <View style={styles.switchRow}>
          <Text variant="titleSmall">{t('config.enableTax')}</Text>
          <Switch value={taxEnabled} onValueChange={setTaxEnabled} color={colors.primary} />
        </View>

        {taxEnabled && (
          <>
            <Text variant="labelLarge" style={[styles.sectionTitle, {marginTop: spacing.md}]}>
              {t('config.taxName').toUpperCase()}
            </Text>
            <Text variant="bodySmall" style={styles.description}>{t('config.taxNameDesc')}</Text>
            <TextInput
              label={t('config.taxName')}
              value={taxName}
              onChangeText={setTaxName}
              mode="outlined"
              style={styles.input}
            />

            <Text variant="labelLarge" style={styles.sectionTitle}>
              {t('config.taxNumber').toUpperCase()}
            </Text>
            <Text variant="bodySmall" style={styles.description}>{t('config.taxNumberDesc')}</Text>
            <TextInput
              label={t('config.taxNumber')}
              value={taxNumber}
              onChangeText={setTaxNumber}
              mode="outlined"
              style={styles.input}
            />

            <Text variant="labelLarge" style={[styles.sectionTitle, {marginTop: spacing.md}]}>
              {t('config.taxes').toUpperCase()}
            </Text>
            <Text variant="bodySmall" style={styles.description}>{t('config.taxesDesc')}</Text>

            {taxLines.map((line, index) => (
              <View key={index} style={styles.taxLineRow}>
                <TextInput
                  mode="outlined"
                  placeholder="Name"
                  value={line.name}
                  onChangeText={v => updateTaxLine(index, 'name', v)}
                  style={[styles.input, styles.taxNameInput]}
                  dense
                />
                <TextInput
                  mode="outlined"
                  placeholder={t('config.taxPercent')}
                  value={String(line.percentage)}
                  onChangeText={v => updateTaxLine(index, 'percentage', v)}
                  keyboardType="numeric"
                  style={[styles.input, styles.taxPercentInput]}
                  dense
                />
                <IconButton
                  icon="delete-outline"
                  size={20}
                  iconColor={colors.error}
                  onPress={() => removeTaxLine(index)}
                  style={{margin: 0}}
                />
              </View>
            ))}

            <Button
              mode="text"
              icon="plus"
              compact
              onPress={addTaxLine}
              style={styles.addButton}
              labelStyle={{fontSize: 13}}>
              {t('config.addTax')}
            </Button>
          </>
        )}

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving || isLoading}
          style={styles.saveBtn}
          buttonColor={colors.primary}>
          {t('common.save')}
        </Button>
      </ScrollView>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={2000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  content: {padding: spacing.md},
  sectionTitle: {color: colors.textSecondary, marginBottom: spacing.xs},
  description: {color: colors.textSecondary, marginBottom: spacing.sm},
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  taxLineRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  taxNameInput: {flex: 2},
  taxPercentInput: {flex: 1},
  addButton: {alignSelf: 'flex-start', marginBottom: spacing.sm},
  saveBtn: {marginTop: spacing.md},
});

export default TaxSettingsScreen;
